import { useEffect, useRef } from 'react';
import { useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import Icon from '@/components/Icon';

export function LoginPage() {
  const { login, isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirecting = useRef(false);
  const from = (location.state as { from?: string } | null)?.from;
  // Set by the BFF's OnRemoteFailure recovery when the OIDC callback failed twice in a row
  // (e.g. a login form left open past the state lifetime, then a broken retry). We show an
  // explicit error + retry instead of auto-firing login, which would loop challenge->fail.
  const signinFailed = searchParams.get('error') !== null;

  useEffect(() => {
    // Wait for the auth-status check to complete before deciding.
    if (isLoading) return;
    // Already authenticated (e.g. returned from a successful login) — do NOT re-trigger login.
    // Re-triggering here is what caused the redirect loop. The Navigate below routes onward.
    if (isAuthenticated) return;
    // Sign-in failed terminally — wait for the user to explicitly retry.
    if (signinFailed) return;
    if (redirecting.current) return;
    // Full-page redirect to the BFF login, returning to the originally-requested page.
    redirecting.current = true;
    login(from);
  }, [login, isAuthenticated, isLoading, from, signinFailed]);

  // If we're already authenticated, leave the interstitial instead of looping back into login.
  if (!isLoading && isAuthenticated) {
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  if (signinFailed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
            <Icon name="error" size={32} />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Sign-in failed</h2>
          <p className="text-gray-600 mb-6">
            We couldn't complete your sign-in. This can happen when the login page was left
            open for too long. Please try again.
          </p>
          <button
            onClick={() => login('/')}
            className="inline-flex items-center justify-center gap-2 w-full rounded-md bg-primary px-4 py-2 text-white font-medium hover:opacity-90 transition"
          >
            <Icon name="sync" size={18} />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Redirecting to Login...</h2>
          <p className="text-gray-600">
            You are being redirected to the authentication server.
          </p>
        </div>
      </div>
    </div>
  );
}
